(() => {
  if (customElements.get('wedding-registry-button')) return;
  const sessions = new Map();
  const baseUrl = 'https://intweb.mm.soul-t.net/api/Bodas';
  const readJson = async (response) => {
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  };
  const isFailure = (data) => data === false || data == null || data.success === false ||
    data.Success === false || data.result === false || data.Result === false || data.error || data.Error ||
    (Array.isArray(data.errors) && data.errors.length > 0);
  const normalizeCatalog = (data) => {
    const items = Array.isArray(data) ? data : data?.Result ?? data?.result;
    if (isFailure(data) || !Array.isArray(items)) throw new Error('Invalid catalog response');
    return items;
  };
  const findItem = (items, sku) => items.find((item) =>
    String(item.itemCode ?? item.ItemCode ?? item.codigo ?? item.Codigo ?? '') === sku);
  const getSession = (key, eventId) => {
    const identity = JSON.stringify([key, eventId]);
    if (!sessions.has(identity)) {
      const session = {
        items: null, loading: null, listeners: new Set(), pending: new Map(),
        notify() { this.listeners.forEach((listener) => listener()); },
        async load(fresh = false) {
          if (this.loading) {
            if (!fresh) return this.loading;
            await this.loading.catch(() => {});
          }
          if (!fresh && this.items) return this.items;
          const request = (async () => {
            const params = new URLSearchParams({ key, IdEvento: eventId });
            this.items = normalizeCatalog(await readJson(await fetch(`${baseUrl}/DetalleBoda?${params}`)));
            this.notify();
            return this.items;
          })();
          this.loading = request;
          try { return await request; }
          catch (error) { this.items = null; throw error; }
          finally { if (this.loading === request) this.loading = null; }
        },
        async add(sku, quantity) {
          if (this.pending.has(sku)) return this.pending.get(sku);
          const request = (async () => {
            const items = await this.load(true);
            if (findItem(items, sku)) return;
            const result = await readJson(await fetch(`${baseUrl}/AddItemBodaSKU?${new URLSearchParams({ key })}`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ cantidad: quantity, itemCode: sku, idEvento: Number(eventId) }),
            }));
            if (isFailure(result)) throw new Error('Add rejected');
            const updated = await this.load(true);
            if (!findItem(updated, sku)) throw new Error('Addition not confirmed');
          })();
          this.pending.set(sku, request);
          this.notify();
          try { return await request; }
          finally { this.pending.delete(sku); this.notify(); }
        },
      };
      sessions.set(identity, session);
    }
    return sessions.get(identity);
  };
  class WeddingRegistryButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.catalog-add-btn');
      if (!this.button || this.listening) return;
      this.text = this.querySelector('.catalog-btn-text');
      this.loader = this.querySelector('.catalog-btn-loader');
      this.button.setAttribute('aria-live', 'polite');
      this.sku = this.dataset.productSku?.trim();
      const eventId = this.dataset.idEvento?.trim();
      const key = this.dataset.apiKey?.trim();
      if (!this.sku || !key || !/^\d+$/.test(eventId || '') || Number(eventId) <= 0) {
        this.render('Sin registro asociado', true, 'error');
        return;
      }
      this.session = getSession(key, eventId);
      this.busy = false;
      this.unknown = false;
      this.onChange = () => this.update();
      this.onClick = () => this.submit();
      this.session.listeners.add(this.onChange);
      this.button.addEventListener('click', this.onClick);
      this.listening = true;
      this.check();
    }
    disconnectedCallback() {
      this.session?.listeners.delete(this.onChange);
      this.button?.removeEventListener('click', this.onClick);
      this.listening = false;
    }
    render(text, disabled, state) {
      this.text.textContent = text;
      this.button.disabled = disabled;
      this.loader.style.display = state === 'loading' ? 'inline-block' : 'none';
      this.button.classList.toggle('button--loading', state === 'loading');
      this.button.classList.toggle('button--added', state === 'added');
      this.button.classList.toggle('button--error', state === 'error');
      this.button.classList.toggle('button--primary', state !== 'added' && state !== 'error');
    }
    update() {
      if (!this.isConnected) return;
      if (this.busy || this.session.pending.has(this.sku)) {
        this.render('Añadiendo...', true, 'loading');
      } else if (findItem(this.session.items || [], this.sku)) {
        this.render(this.button.dataset.addedText || 'Añadido al registro', true, 'added');
      } else if (!this.unknown) {
        this.render(this.button.dataset.defaultText || 'Añadir al registro', false, 'default');
      }
    }
    async check() {
      this.busy = true;
      this.render('Verificando...', true, 'loading');
      try {
        await this.session.load(this.unknown);
        this.unknown = false;
      } catch (error) {
        this.unknown = true;
        this.render('Error al verificar. Reintentar', false, 'error');
      } finally {
        this.busy = false;
        this.update();
      }
    }
    async submit() {
      if (this.busy || this.button.disabled) return;
      if (this.unknown) { await this.check(); return; }
      const input = document.getElementById(this.dataset.quantityInputId);
      const quantity = input ? Number(input.value) : 1;
      if (!Number.isSafeInteger(quantity) || quantity <= 0 || (input && !input.checkValidity())) {
        input?.reportValidity();
        this.render('Indica una cantidad válida', false, 'error');
        return;
      }
      this.busy = true;
      this.render('Añadiendo...', true, 'loading');
      try {
        await this.session.add(this.sku, quantity);
        this.unknown = false;
      } catch (error) {
        this.unknown = true;
        this.render('No se confirmó el agregado. Verificar', false, 'error');
      } finally {
        this.busy = false;
        this.update();
      }
    }
  }
  customElements.define('wedding-registry-button', WeddingRegistryButton);
})();
